import { HttpApiError, HttpApiGroup } from "@effect/platform";
import { Schema } from "effect";
import { endpoint } from "../auth/endpoint";
import { DataIntegrityError, DatabaseError } from "../auth/errors";

import { UserGroupId } from "../ids/definitions";
import { MongoUserId } from "../ids/mongo-ids";

export const UserGroupResponse = Schema.Struct({
  id: UserGroupId,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  parentId: Schema.NullOr(UserGroupId),
  /** Member user ids, populated by the list endpoint. */
  mongoUserIds: Schema.Array(MongoUserId),
});
export type UserGroupResponse = typeof UserGroupResponse.Type;

const ListUserGroupsResponse = Schema.Struct({
  groups: Schema.Array(UserGroupResponse),
});

const CreateUserGroupInput = Schema.Struct({
  name: Schema.String.pipe(Schema.minLength(1)),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  parentId: Schema.optional(Schema.NullOr(UserGroupId)),
  /** Optional initial members to add after the group is created. */
  mongoUserIds: Schema.optional(Schema.Array(MongoUserId)),
});

const CreateUserGroupResponse = Schema.Struct({ group: UserGroupResponse });

const UpdateUserGroupInput = Schema.Struct({
  name: Schema.optional(Schema.String.pipe(Schema.minLength(1))),
  description: Schema.optional(Schema.NullOr(Schema.String)),
  parentId: Schema.optional(Schema.NullOr(UserGroupId)),
});

const ListUserGroupMembersResponse = Schema.Struct({
  mongoUserIds: Schema.Array(MongoUserId),
});

const AddUserGroupMembersInput = Schema.Struct({
  mongoUserIds: Schema.Array(MongoUserId).pipe(Schema.minItems(1)),
});

export const UserGroupGroup = HttpApiGroup.make("userGroup")
  .add(
    endpoint.merchant
      .get("listUserGroups", "/user-groups")

      .addSuccess(ListUserGroupsResponse)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("createUserGroup", "/user-groups")

      .setPayload(CreateUserGroupInput)
      .addSuccess(CreateUserGroupResponse)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .patch("updateUserGroup", "/user-groups/:id")

      .setPath(Schema.Struct({ id: UserGroupId }))
      .setPayload(UpdateUserGroupInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("deleteUserGroup", "/user-groups/:id")

      .setPath(Schema.Struct({ id: UserGroupId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .get("listUserGroupMembers", "/user-groups/:id/members")

      .setPath(Schema.Struct({ id: UserGroupId }))
      .addSuccess(ListUserGroupMembersResponse)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .post("addUserGroupMembers", "/user-groups/:id/members")

      .setPath(Schema.Struct({ id: UserGroupId }))
      .setPayload(AddUserGroupMembersInput)
      .addSuccess(Schema.Void)
      .addError(HttpApiError.BadRequest)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .add(
    endpoint.merchant
      .del("removeUserGroupMember", "/user-groups/:id/members/:mongoUserId")

      .setPath(Schema.Struct({ id: UserGroupId, mongoUserId: MongoUserId }))
      .addSuccess(Schema.Void)
      .addError(HttpApiError.NotFound)
      .addError(HttpApiError.Forbidden)
      .addError(DatabaseError)
      .addError(DataIntegrityError),
  )
  .prefix("/api");
